import React from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { KnzComponentProps } from '../../types';

type CalloutType = 'info' | 'warning' | 'important' | 'success';

const CONFIG: Record<CalloutType, { color: string; icon: React.ReactNode }> = {
  info: {
    color: 'info.main',
    icon: <InfoOutlinedIcon sx={{ fontSize: 20, color: 'info.main', mt: 0.2 }} />,
  },
  warning: {
    color: 'warning.main',
    icon: <WarningAmberIcon sx={{ fontSize: 20, color: 'warning.main', mt: 0.2 }} />,
  },
  important: {
    color: 'error.main',
    icon: <ErrorOutlineIcon sx={{ fontSize: 20, color: 'error.main', mt: 0.2 }} />,
  },
  success: {
    color: 'success.main',
    icon: <CheckCircleOutlineIcon sx={{ fontSize: 20, color: 'success.main', mt: 0.2 }} />,
  },
};

/**
 * 注意・補足ボックス（WorkflowInfoBox ベース）。
 * params: type（info/warning/important/success）、text（joined, 任意）
 * children: 配下ブロック（任意）。text と children は併用可（text の下に children を描画）。
 */
export const Callout: React.FC<KnzComponentProps> = ({ node, renderInline, renderChildren }) => {
  const typeParam = (node.params.type as string) || 'info';
  const type: CalloutType = (['info', 'warning', 'important', 'success'] as const).includes(
    typeParam as CalloutType,
  )
    ? (typeParam as CalloutType)
    : 'info';
  const { color, icon } = CONFIG[type];
  const text = (node.params.text as string) ?? '';
  const hasChildren = node.children.length > 0;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderLeft: 3,
        borderLeftColor: color,
        borderRadius: 2,
        p: 2,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        {icon}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {text && (
            <Typography
              variant="body2"
              color="text.secondary"
              component="div"
              sx={{ lineHeight: 1.7 }}
            >
              {renderInline(text)}
            </Typography>
          )}
          {hasChildren && (
            <Stack spacing={1.5} sx={{ mt: text ? 1.5 : 0 }}>
              {renderChildren(node.children)}
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default Callout;
